import React, { useState, useEffect, useCallback } from "react";
import { map } from "lodash";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "../../config/axios";
import Loading from "components/Loading";
import { TextField, Table, TableBody, TableCell, TableHead, TableRow, InputAdornment, Button, Paper, Typography, Box, Switch, ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { getProductTypes, updateProductTypeCosts } from "../../redux/action/order.action";
import "./OneTimeCost.css";

const OneTimeCost = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const ORDER_STATE = useSelector(state => state.order);
  const [productTypes, setProductTypes] = useState([]);
  const [editedProducts, setEditedProducts] = useState({});
  const [partialReturnRules, setPartialReturnRules] = useState([]);
  const [loadingPartial, setLoadingPartial] = useState(false);
  const [savingPartial, setSavingPartial] = useState(false);
  const [partialReturnBoxesEnabled, setPartialReturnBoxesEnabled] = useState(true);
  const [partialReturnSpacesEnabled, setPartialReturnSpacesEnabled] = useState(true);

  useEffect(() => {
    dispatch(getProductTypes());
  }, [dispatch]);

  const fetchPartialReturnRules = useCallback(async () => {
    try {
      setLoadingPartial(true);
      const res = await axios.get("/admin/partial-return-rules");
      if (res.data && res.data.data) {
        const data = res.data.data;
        const normalized = data.map(r => ({
          ...r,
          free_partial_returns: r.free_partial_returns ?? (r.product_type === "space" ? (r.all_free ? 1 : 0) : r.free_units) ?? 0,
          min_initial_units: r.product_type === "box" ? (r.min_initial_units ?? r.min_units ?? 1) : null,
          enabled: r.enabled !== false
        }));
        setPartialReturnRules(normalized);
        const firstBox = normalized.find(r => r.product_type === "box");
        const firstSpace = normalized.find(r => r.product_type === "space");
        if (firstBox != null) setPartialReturnBoxesEnabled(firstBox.enabled !== false);
        if (firstSpace != null) setPartialReturnSpacesEnabled(firstSpace.enabled !== false);
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to fetch partial return rules", { variant: "error" });
    } finally {
      setLoadingPartial(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchPartialReturnRules();
  }, [fetchPartialReturnRules]);

  const productTypesFromState = ORDER_STATE?.productTypes;
  useEffect(() => {
    if (productTypesFromState?.length > 0) {
      setProductTypes(productTypesFromState);
    }
  }, [productTypesFromState]);

  const handleFieldChange = (productId, fieldType, fieldName, value) => {
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (value !== "" && !regex.test(value)) return;

    const updatedProducts = [...productTypes];
    const productIndex = updatedProducts.findIndex(p => p._id === productId);

    if (productIndex === -1) return;

    if (fieldType === "premium_service_cost") {
      updatedProducts[productIndex].premium_service_cost = {
        ...updatedProducts[productIndex].premium_service_cost,
        [fieldName]: value === "" ? null : Number(value)
      };
    } else if (fieldType === "service_cost") {
      updatedProducts[productIndex].service_cost = {
        ...updatedProducts[productIndex].service_cost,
        [fieldName]: value === "" ? null : Number(value)
      };
    } else if (fieldType === "return_cost") {
      updatedProducts[productIndex].return_cost = {
        ...updatedProducts[productIndex].return_cost,
        [fieldName]: value === "" ? null : Number(value)
      };
    }

    setProductTypes(updatedProducts);

    // Track edited products
    const editedProduct = editedProducts[productId] || {
      premium_service_cost: { ...updatedProducts[productIndex].premium_service_cost },
      service_cost: { ...updatedProducts[productIndex].service_cost },
      return_cost: { ...updatedProducts[productIndex].return_cost },
    };

    if (fieldType === "premium_service_cost") {
      editedProduct.premium_service_cost = {
        ...editedProduct.premium_service_cost,
        [fieldName]: value === "" ? null : Number(value)
      };
    } else if (fieldType === "service_cost") {
      editedProduct.service_cost = {
        ...editedProduct.service_cost,
        [fieldName]: value === "" ? null : Number(value)
      };
    } else if (fieldType === "return_cost") {
      editedProduct.return_cost = {
        ...editedProduct.return_cost,
        [fieldName]: value === "" ? null : Number(value)
      };
    }

    setEditedProducts({
      ...editedProducts,
      [productId]: editedProduct
    });
  };

  const handleSave = productId => {
    const editedProduct = editedProducts[productId];
    if (!editedProduct) return;

    const payload = {
      _id: productId,
      premium_service_cost: editedProduct.premium_service_cost,
      service_cost: editedProduct.service_cost,
      return_cost: editedProduct.return_cost
    };

    dispatch(
      updateProductTypeCosts({
        payload,
        handleSuccess: () => {
          // Remove from edited products after successful save
          const newEditedProducts = { ...editedProducts };
          delete newEditedProducts[productId];
          setEditedProducts(newEditedProducts);
          enqueueSnackbar(t("oneTimeCost.updateSuccess"), { variant: "success" });
        },
        handleFailure: error => {
          enqueueSnackbar(error || t("oneTimeCost.updateError"), { variant: "error" });
        }
      })
    );
  };

  const handlePartialRuleChange = (index, field, value) => {
    setPartialReturnRules(prev => {
      const next = [...prev];
      const numFields = ["free_partial_returns", "min_subscription_months", "min_initial_units"];
      next[index] = { ...next[index], [field]: numFields.includes(field) ? (value === "" ? null : Number(value)) : (field === "enabled" ? Boolean(value) : value) };
      return next;
    });
  };

  const validatePartialReturnRules = () => {
    const errors = [];
    partialReturnRules.forEach((r, idx) => {
      const row = idx + 1;
      const months = r.min_subscription_months;
      if (months == null || typeof months !== "number" || months < 1) {
        errors.push(t("oneTimeCost.partialReturnValidationMinMonths", { row }));
      }
      const freePartial = r.free_partial_returns;
      if (freePartial == null || typeof freePartial !== "number" || freePartial < 0) {
        errors.push(t("oneTimeCost.partialReturnValidationFreeReturns", { row }));
      }
      if (r.product_type === "box") {
        const minInitial = r.min_initial_units;
        if (minInitial == null || typeof minInitial !== "number" || minInitial < 1) {
          errors.push(t("oneTimeCost.partialReturnValidationMinInitialBoxes", { row }));
        }
      }
    });
    return errors;
  };

  const handleSavePartialRules = async () => {
    const validationErrors = validatePartialReturnRules();
    if (validationErrors.length > 0) {
      enqueueSnackbar(validationErrors.join(" "), { variant: "error" });
      return;
    }
    try {
      setSavingPartial(true);
      const payload = partialReturnRules.map(r => {
        const isBox = r.product_type === "box";
        const isSpace = r.product_type === "space";
        return {
          product_type: r.product_type,
          free_partial_returns: r.free_partial_returns,
          min_subscription_months: r.min_subscription_months,
          min_initial_units: isSpace ? null : (r.min_initial_units ?? null),
          enabled: isBox ? partialReturnBoxesEnabled : partialReturnSpacesEnabled,
        };
      });
      await axios.patch("/admin/partial-return-rules", { rules: payload });
      enqueueSnackbar(t("oneTimeCost.updateSuccess") || "Partial return rules saved", { variant: "success" });
      fetchPartialReturnRules();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to save partial return rules", { variant: "error" });
    } finally {
      setSavingPartial(false);
    }
  };

  if (ORDER_STATE?.fetchingProductTypesLoading) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  return (
    <div className="one-time-cost-container">
      <Paper square>
        <Box p={3}>
          <Typography variant="h5" gutterBottom>
            {t("oneTimeCost.title")}
          </Typography>

          <div style={{ display: "block", overflowX: "auto", marginTop: 20 }}>
            <Table>
              <TableHead>
                <TableRow className="cost-table__head-row">
                  <TableCell>{t("oneTimeCost.image")}</TableCell>
                  <TableCell>{t("oneTimeCost.name")}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, textTransform: "uppercase" }}>{t("oneTimeCost.recolhaServiceCost")}</Typography>
                    <Box display="flex" flexDirection="column" style={{ marginTop: "8px" }}>
                      <Typography variant="caption">{t("oneTimeCost.hoursIncluded")}</Typography>
                      <Typography variant="caption">{t("oneTimeCost.extraHourCost")} (Custo por hora extra)</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, textTransform: "uppercase" }}>{t("oneTimeCost.premiumPickupServiceCost")}</Typography>
                    <Box display="flex" flexDirection="column" style={{ marginTop: "8px" }}>
                      <Typography variant="caption">{t("oneTimeCost.amount")} (Valor)</Typography>
                      <Typography variant="caption">{t("oneTimeCost.hoursIncluded")}</Typography>
                      <Typography variant="caption">{t("oneTimeCost.extraHourCost")} (Custo por hora extra)</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, textTransform: "uppercase" }}>{t("oneTimeCost.returnCost")}</Typography>
                    <Box display="flex" flexDirection="column" style={{ marginTop: "8px" }}>
                      <Typography variant="caption">{t("oneTimeCost.monthly")}</Typography>
                      <Typography variant="caption">{t("oneTimeCost.quarterly")}</Typography>
                      <Typography variant="caption">{t("oneTimeCost.semester")}</Typography>
                      <Typography variant="caption">{t("oneTimeCost.annual")}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{t("oneTimeCost.action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {map(productTypes, (product) => {
                  const imageUrl = product.sm_image_url || product.image_url || `https://keepwhat-cdn.s3.eu-west-2.amazonaws.com/img/all-products/${product.type}.svg`;
                  const edited = editedProducts[product._id];
                  const isEdited = edited && (
                    edited.premium_service_cost !== undefined ||
                    edited.service_cost !== undefined ||
                    edited.return_cost !== undefined
                  );
                  const isSaving = ORDER_STATE?.updatingProductTypeCostsLoading?.[product._id] || false;

                  return (
                    <TableRow key={product._id} className="cost-table__row">
                      <TableCell>
                        <img style={{ height: "70px", width: "auto" }} alt={product.name} src={imageUrl} />
                      </TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <TextField
                            label={t("oneTimeCost.hoursIncluded")}
                            value={product.service_cost?.hours_included ?? ""}
                            onChange={e => handleFieldChange(product._id, "service_cost", "hours_included", e.target.value)}
                            variant="outlined"
                            size="small"
                            type="number"
                            style={{ marginBottom: "8px" }}
                          />
                          <TextField
                            label={t("oneTimeCost.extraHourCost")}
                            value={product.service_cost?.extra_hour_cost ?? ""}
                            onChange={e => handleFieldChange(product._id, "service_cost", "extra_hour_cost", e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>
                            }}
                            variant="outlined"
                            size="small"
                            type="number"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <TextField
                            label={t("oneTimeCost.amount")}
                            value={product.premium_service_cost?.amount ?? ""}
                            onChange={e => handleFieldChange(product._id, "premium_service_cost", "amount", e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>
                            }}
                            variant="outlined"
                            size="small"
                            type="number"
                            style={{ marginBottom: "8px" }}
                          />
                          <TextField
                            label={t("oneTimeCost.hoursIncluded")}
                            value={product.premium_service_cost?.hours_included ?? ""}
                            onChange={e => handleFieldChange(product._id, "premium_service_cost", "hours_included", e.target.value)}
                            variant="outlined"
                            size="small"
                            type="number"
                            style={{ marginBottom: "8px" }}
                          />
                          <TextField
                            label={t("oneTimeCost.extraHourCost")}
                            value={product.premium_service_cost?.extra_hour_cost ?? ""}
                            onChange={e => handleFieldChange(product._id, "premium_service_cost", "extra_hour_cost", e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>
                            }}
                            variant="outlined"
                            size="small"
                            type="number"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <TextField
                            label={t("oneTimeCost.monthly")}
                            value={product.return_cost?.monthly ?? ""}
                            onChange={e => handleFieldChange(product._id, "return_cost", "monthly", e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>
                            }}
                            variant="outlined"
                            size="small"
                            type="number"
                            style={{ marginBottom: "8px" }}
                          />
                          <TextField
                            label={t("oneTimeCost.quarterly")}
                            value={product.return_cost?.quarterly ?? ""}
                            onChange={e => handleFieldChange(product._id, "return_cost", "quarterly", e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>
                            }}
                            variant="outlined"
                            size="small"
                            type="number"
                            style={{ marginBottom: "8px" }}
                          />
                          <TextField
                            label={t("oneTimeCost.semester")}
                            value={product.return_cost?.semester ?? ""}
                            onChange={e => handleFieldChange(product._id, "return_cost", "semester", e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>
                            }}
                            variant="outlined"
                            size="small"
                            type="number"
                            style={{ marginBottom: "8px" }}
                          />
                          <TextField
                            label={t("oneTimeCost.annual")}
                            value={product.return_cost?.annual ?? ""}
                            onChange={e => handleFieldChange(product._id, "return_cost", "annual", e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>
                            }}
                            variant="outlined"
                            size="small"
                            type="number"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button onClick={() => handleSave(product._id)} variant="contained" color="primary" disabled={!isEdited || isSaving} size="small">
                          {isSaving ? t("oneTimeCost.saving") : t("oneTimeCost.save")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <Typography variant="h6" style={{ marginTop: 32, marginBottom: 16 }}>
            {t("oneTimeCost.partialReturnRules")}
          </Typography>
          <ExpansionPanel style={{ marginBottom: 16 }}>
            <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">{t("oneTimeCost.partialReturnInfoTitle")}</Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Box>
                <Typography variant="body2" paragraph><strong>{t("oneTimeCost.partialReturnHowItWorks")}</strong></Typography>
                <Typography variant="body2" paragraph component="span">{t("oneTimeCost.partialReturnHowItWorksDesc")}</Typography>
                <Typography variant="body2" paragraph><strong>{t("oneTimeCost.partialReturnTracking")}</strong></Typography>
                <Typography variant="body2" paragraph component="span">{t("oneTimeCost.partialReturnTrackingDesc")}</Typography>
                <Typography variant="body2" paragraph><strong>{t("oneTimeCost.partialReturnExamples")}</strong></Typography>
                <Typography variant="body2" component="span" style={{ whiteSpace: "pre-line" }}>{t("oneTimeCost.partialReturnExamplesDesc")}</Typography>
              </Box>
            </ExpansionPanelDetails>
          </ExpansionPanel>
          {loadingPartial ? (
            <Loading />
          ) : (
            <>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 12 }}>
                {t("oneTimeCost.partialReturnTableHelp")}
              </Typography>
              {(() => {
                const boxIndices = partialReturnRules.map((r, i) => i).filter(i => partialReturnRules[i].product_type === "box");
                const spaceIndices = partialReturnRules.map((r, i) => i).filter(i => partialReturnRules[i].product_type === "space");
                return (
                  <>
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8, marginTop: 8 }}>
                      {t("oneTimeCost.productTypeBox")}
                    </Typography>
                    <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
                      <Switch
                        checked={partialReturnBoxesEnabled}
                        onChange={e => setPartialReturnBoxesEnabled(e.target.checked)}
                        color="primary"
                      />
                      <Typography variant="body2">{t("oneTimeCost.partialReturnEnable")} (Caixas)</Typography>
                    </Box>
                    <Table size="small" style={{ marginBottom: 24 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("oneTimeCost.partialReturnColFreeReturns")}</TableCell>
                          <TableCell>{t("oneTimeCost.partialReturnColMinMonths")}</TableCell>
                          <TableCell>{t("oneTimeCost.partialReturnColMinInitialBoxes")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {boxIndices.map((idx) => {
                          const r = partialReturnRules[idx];
                          return (
                            <TableRow key={r._id || idx}>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={r.free_partial_returns ?? (r.product_type === "space" ? (r.all_free ? 1 : 0) : r.free_units) ?? ""}
                                  onChange={e => handlePartialRuleChange(idx, "free_partial_returns", e.target.value)}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={r.min_subscription_months ?? ""}
                                  onChange={e => handlePartialRuleChange(idx, "min_subscription_months", e.target.value)}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={r.min_initial_units ?? r.min_units ?? ""}
                                  onChange={e => handlePartialRuleChange(idx, "min_initial_units", e.target.value)}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                      {t("oneTimeCost.productTypeSpace")}
                    </Typography>
                    <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
                      <Switch
                        checked={partialReturnSpacesEnabled}
                        onChange={e => setPartialReturnSpacesEnabled(e.target.checked)}
                        color="primary"
                      />
                      <Typography variant="body2">{t("oneTimeCost.partialReturnEnable")} (Espaço)</Typography>
                    </Box>
                    <Table size="small" style={{ marginBottom: 24 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("oneTimeCost.partialReturnColFreeReturns")}</TableCell>
                          <TableCell>{t("oneTimeCost.partialReturnColMinMonths")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {spaceIndices.map((idx) => {
                          const r = partialReturnRules[idx];
                          return (
                            <TableRow key={r._id || idx}>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={r.free_partial_returns ?? (r.product_type === "space" ? (r.all_free ? 1 : 0) : r.free_units) ?? ""}
                                  onChange={e => handlePartialRuleChange(idx, "free_partial_returns", e.target.value)}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={r.min_subscription_months ?? ""}
                                  onChange={e => handlePartialRuleChange(idx, "min_subscription_months", e.target.value)}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                );
              })()}
              <Button variant="contained" color="primary" onClick={handleSavePartialRules} disabled={savingPartial} style={{ marginTop: 12 }}>
                {savingPartial ? t("oneTimeCost.saving") || "Saving…" : t("oneTimeCost.save")}
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </div>
  );
};

export default OneTimeCost;
